import { Box } from '@mui/material'
import ButtonMobileComponent from 'components/button-mobile' // Import your ButtonMobileComponent
import CheckboxComponent from 'components/checkbox'
import InputMobileComponent from 'components/input-mobile'
import { useEffect, useState } from 'react'
import { UpdateSchoolInputProps } from 'types/SchoolTypes'
import SettingsBox from './SettingsBox'
import { isValidSchoolCode } from 'utils/validations'

interface RegistrationSettingsProps {
  initialIsUserOpenEnrollment: boolean
  initialSchoolCode?: string | null
  onSave: (body: UpdateSchoolInputProps, loadingMsg?: string, cb?: () => void) => Promise<void>
}

function RegistrationSettings({ initialIsUserOpenEnrollment, initialSchoolCode, onSave }: RegistrationSettingsProps) {
  const [isUserOpenEnrollment, setIsUserOpenEnrollment] = useState<boolean>(initialIsUserOpenEnrollment)
  const [schoolCode, setSchoolCode] = useState<string | null>(initialSchoolCode ?? null)

  // Sync local state with props when props change
  useEffect(() => {
    setIsUserOpenEnrollment(initialIsUserOpenEnrollment)
    setSchoolCode(initialSchoolCode ?? null)
  }, [initialIsUserOpenEnrollment, initialSchoolCode])

  // hasChanges is always derived
  const hasChanges: boolean = isUserOpenEnrollment !== initialIsUserOpenEnrollment || schoolCode !== initialSchoolCode

  // Handlers
  const isInvalid = !isValidSchoolCode(schoolCode ?? '', true)
  const handleSave = async () => {
    if (hasChanges && !isInvalid) {
      await onSave({ schoolCode: isUserOpenEnrollment ? schoolCode : null }, 'Updating self-registration settings...')
    }
  }

  return (
    <SettingsBox title='Self-Service Enrollment Method' subtitle='Enable families to self-register with access code'>
      <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
        <CheckboxComponent
          isChecked={!isUserOpenEnrollment}
          onClick={() => {
            if (isUserOpenEnrollment === true) setIsUserOpenEnrollment(!isUserOpenEnrollment)
          }}
          label={`Restricted (User-Specific Codes)`}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
        <CheckboxComponent
          isChecked={isUserOpenEnrollment}
          onClick={() => {
            if (isUserOpenEnrollment === false) setIsUserOpenEnrollment(!isUserOpenEnrollment)
          }}
          label={`Open (School-Wide Code)`}
        />
      </Box>

      {isUserOpenEnrollment && (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', mb: 2, gap: 2 }}>
          <InputMobileComponent
            labeltext={hasChanges ? 'Enter School Code' : 'School Code'}
            value={schoolCode}
            onChange={setSchoolCode}
            isError={isInvalid}
            helperText={
              schoolCode
                ? 'Share code only with school families. Update periodically if needed.'
                : 'Create a unique code. Share with school families for verified signup.'
            }
            data_testid='school-secret-code'
          />
        </Box>
      )}

      {hasChanges && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <ButtonMobileComponent text='Save' width='w-[100px]' onClick={handleSave} disabled={isInvalid} />
        </Box>
      )}
    </SettingsBox>
  )
}

export default RegistrationSettings
